// Profile control for the workspace header. Presentational: it shows the real
// user data it is given and calls the handlers it is given (ProfileModal opener,
// the existing AuthContext logout, and the original "Back" navigation).
// Desktop: dropdown. Phones: bottom sheet (same markup, CSS decides).
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ChevronDown, LogOut, UserRound } from "lucide-react";

export const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

export const Avatar = ({ name, picture, large = false }) => {
  const [failedSrc, setFailedSrc] = useState(null);
  const showImage = Boolean(picture) && failedSrc !== picture;
  return (
    <span className={`cc-avatar${large ? " cc-avatar--lg" : ""}`} aria-hidden="true">
      {showImage ? <img src={picture} alt="" onError={() => setFailedSrc(picture)} /> : initialsOf(name)}
    </span>
  );
};

const ProfileMenu = ({ name, email, roleText, picture, onOpenProfile, onBack, onLogout }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (open) menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [open]);

  const onKeyDown = (event) => {
    const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
    const index = items.indexOf(document.activeElement);
    if (event.key === "Escape") {
      event.stopPropagation();
      close();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      items[(index + 1) % items.length]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      items[(index - 1 + items.length) % items.length]?.focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      items[0]?.focus();
    } else if (event.key === "End") {
      event.preventDefault();
      items[items.length - 1]?.focus();
    } else if (event.key === "Tab") {
      // Leaving the menu with Tab dismisses it; focus continues from the trigger.
      close();
    }
  };

  const run = (action) => () => {
    setOpen(false);
    action();
  };

  return (
    <div className="cc-profile">
      <button
        type="button"
        ref={buttonRef}
        className="cc-profile__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Account menu for ${name}`}
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar name={name} picture={picture} />
        <span className="cc-profile__meta">
          <span className="cc-profile__name">{name}</span>
          <span className="cc-profile__role">{roleText}</span>
        </span>
        <span className="cc-profile__chev" aria-hidden="true">
          <ChevronDown />
        </span>
      </button>

      {open && createPortal(
        <>
          <div className="cc-menu-backdrop" aria-hidden="true" onClick={() => close(false)} />
          <div id={menuId} ref={menuRef} className="cc-menu" role="menu" aria-label="Account" onKeyDown={onKeyDown}>
            <div className="cc-menu__grab" aria-hidden="true" />
            <div className="cc-menu__head">
              <Avatar name={name} picture={picture} large />
              <div className="cc-menu__who">
                <div className="cc-menu__name">{name}</div>
                {email ? <div className="cc-menu__email">{email}</div> : null}
                {roleText ? <span className="cc-menu__role">{roleText}</span> : null}
              </div>
            </div>
            <button type="button" role="menuitem" className="cc-menu__item" onClick={run(onOpenProfile)}>
              <UserRound aria-hidden="true" /> Profile &amp; Password
            </button>
            <button type="button" role="menuitem" className="cc-menu__item" onClick={run(onBack)}>
              <ArrowLeft aria-hidden="true" /> Back to home
            </button>
            <div className="cc-menu__sep" role="separator" />
            <button type="button" role="menuitem" className="cc-menu__item cc-menu__item--danger" onClick={run(onLogout)}>
              <LogOut aria-hidden="true" /> Log out
            </button>
          </div>
        </>,
        // Rendered inside the workspace root (so the scoped theme variables still
        // apply) but outside the sticky header, whose stacking context would
        // otherwise keep the sheet underneath the bottom navigation.
        buttonRef.current?.closest(".cc-ws") || document.body
      )}
    </div>
  );
};

export default ProfileMenu;
